import { useMemo, useState } from 'react';
import {
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
  ReferenceLine,
  ZAxis,
  Label,
} from 'recharts';
import { Rocket, Sparkles } from 'lucide-react';
import { cn, formatCompact } from '@/lib/utils';
import { useGSCTopQueries } from '@/hooks/useGSCData';
import { useAuthStore } from '@/stores/auth-store';

interface KeywordPoint {
  query: string;
  x: number; // Impressions
  y: number; // Position (inverted - 1 at top, 50 at bottom)
  z: number; // Clicks (bubble size)
  clicks: number;
  impressions: number;
  ctr: number;
  position: number;
  category: 'winner' | 'opportunity';
}

const PAGE_1_CUTOFF = 10;

/**
 * Keyword Galaxy View - Scatter plot inspired by the ChartZen design
 * X-axis: Impressions (search volume)
 * Y-axis: Position (1 at top, 50 at bottom - inverted scale)
 * Bubble size: Clicks
 *
 * Categories:
 * - Winners (green): Position <= 10 (Page 1)
 * - Opportunities (yellow): Position > 10 (Page 2+)
 */
export function KeywordGalaxyView() {
  const { activePropertyId, properties } = useAuthStore();
  const activeProperty = properties.find((p) => p.id === activePropertyId);
  const hasGSC = !!activeProperty?.gscSite;

  const { data: queries, isLoading } = useGSCTopQueries(100);
  const [hoveredKeyword, setHoveredKeyword] = useState<KeywordPoint | null>(null);

  // Transform data for scatter chart
  const chartData = useMemo(() => {
    if (!queries) return [];

    return queries.map((q): KeywordPoint => {
      const isWinner = q.position <= PAGE_1_CUTOFF;

      return {
        query: q.query,
        x: q.impressions,
        y: q.position, // Keep actual position, Y-axis will be reversed
        z: Math.sqrt(q.clicks) * 5 + 10, // Scale for visual
        clicks: q.clicks,
        impressions: q.impressions,
        ctr: q.ctr,
        position: q.position,
        category: isWinner ? 'winner' : 'opportunity',
      };
    });
  }, [queries]);

  // Calculate stats
  const stats = useMemo(() => {
    const winners = chartData.filter((d) => d.category === 'winner');
    const opportunities = chartData.filter((d) => d.category === 'opportunity');

    // Find high potential opportunities (high impressions, low position)
    const highPotential = opportunities
      .filter((d) => d.position > PAGE_1_CUTOFF && d.position <= 20) // Positions 11-20
      .sort((a, b) => b.impressions - a.impressions)
      .slice(0, 5);

    return {
      winners: winners.length,
      opportunities: opportunities.length,
      highPotential,
    };
  }, [chartData]);

  const getColor = (category: string) => {
    return category === 'winner' ? '#22c55e' : '#eab308';
  };

  if (!hasGSC) {
    return (
      <div className="flex h-full items-center justify-center text-muted-foreground">
        <div className="text-center">
          <Rocket className="h-12 w-12 mx-auto mb-4 opacity-50" />
          <p>No Search Console property connected</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 h-full flex flex-col">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-foreground flex items-center gap-2">
            <Rocket className="h-6 w-6 text-purple-500" />
            Keyword Galaxy
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Position (Y) vs. Impressions (X)
          </p>
        </div>

        {/* Legend */}
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-success" />
            <span className="text-sm text-foreground">Winner</span>
            <span className="text-xs text-muted-foreground">({stats.winners})</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-warning" />
            <span className="text-sm text-foreground">Opportunity</span>
            <span className="text-xs text-muted-foreground">({stats.opportunities})</span>
          </div>
        </div>
      </div>

      {/* Chart */}
      <div className="glass-panel p-4 flex-1 min-h-[400px] relative">
        {isLoading ? (
          <div className="h-full flex items-center justify-center text-muted-foreground animate-pulse">
            Loading galaxy...
          </div>
        ) : (
          <>
            <ResponsiveContainer width="100%" height="100%">
              <ScatterChart margin={{ top: 20, right: 40, bottom: 60, left: 60 }}>
                {/* Page 1 Cutoff Line */}
                <ReferenceLine
                  y={PAGE_1_CUTOFF}
                  stroke="#f97316"
                  strokeDasharray="8 4"
                  strokeWidth={2}
                >
                  <Label
                    value="Page 1 Cutoff"
                    position="right"
                    fill="#f97316"
                    fontSize={12}
                    fontWeight={500}
                  />
                </ReferenceLine>

                <XAxis
                  type="number"
                  dataKey="x"
                  name="Impressions"
                  domain={[0, 'dataMax']}
                  tickFormatter={(v) => formatCompact(v)}
                  stroke="#4b5563"
                  tick={{ fill: '#9ca3af', fontSize: 11 }}
                  axisLine={{ stroke: '#374151' }}
                >
                  <Label
                    value="Impressions →"
                    position="bottom"
                    offset={30}
                    fill="#9ca3af"
                    fontSize={12}
                  />
                </XAxis>
                <YAxis
                  type="number"
                  dataKey="y"
                  name="Position"
                  domain={[1, 50]}
                  reversed // Position 1 at top!
                  tickFormatter={(v) => v.toString()}
                  stroke="#4b5563"
                  tick={{ fill: '#9ca3af', fontSize: 11 }}
                  axisLine={{ stroke: '#374151' }}
                  ticks={[1, 10, 20, 30, 40, 50]}
                >
                  <Label
                    value="← Google Rank"
                    angle={-90}
                    position="left"
                    offset={35}
                    fill="#9ca3af"
                    fontSize={12}
                  />
                </YAxis>
                <ZAxis type="number" dataKey="z" range={[30, 500]} />
                <Tooltip content={<CustomTooltip />} />
                <Scatter
                  data={chartData}
                  onMouseEnter={(data) => setHoveredKeyword(data as KeywordPoint)}
                  onMouseLeave={() => setHoveredKeyword(null)}
                >
                  {chartData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={getColor(entry.category)}
                      fillOpacity={hoveredKeyword?.query === entry.query ? 1 : 0.7}
                      stroke={hoveredKeyword?.query === entry.query ? '#fff' : 'none'}
                      strokeWidth={2}
                      style={{ cursor: 'pointer' }}
                    />
                  ))}
                </Scatter>
              </ScatterChart>
            </ResponsiveContainer>

            {/* Chart annotations */}
            <div className="absolute bottom-4 right-4 text-xs text-muted-foreground">
              <p>Large bubble = More clicks</p>
              <p>Higher up = Better rank</p>
            </div>
          </>
        )}
      </div>

      {/* High Potential Keywords */}
      {stats.highPotential.length > 0 && (
        <div className="glass-panel p-4">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="h-4 w-4 text-warning" />
            <span className="font-medium text-foreground">High Potential Keywords</span>
            <span className="text-xs text-muted-foreground">(Page 2 with high impressions)</span>
          </div>
          <div className="grid grid-cols-5 gap-3">
            {stats.highPotential.map((kw) => (
              <HighPotentialCard key={kw.query} keyword={kw} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function HighPotentialCard({ keyword }: { keyword: KeywordPoint }) {
  return (
    <div className="p-3 rounded-lg bg-warning/10 border border-warning/30">
      <p className="text-sm font-medium text-foreground truncate" title={keyword.query}>
        "{keyword.query}"
      </p>
      <div className="mt-2 space-y-1 text-xs">
        <div className="flex justify-between">
          <span className="text-muted-foreground">Impressions:</span>
          <span className="text-foreground font-medium">{formatCompact(keyword.impressions)}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-muted-foreground">Avg Position:</span>
          <span className="text-warning font-medium">#{keyword.position.toFixed(1)}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-muted-foreground">Clicks:</span>
          <span className="text-foreground">{keyword.clicks}</span>
        </div>
      </div>
      <div className="mt-2 pt-2 border-t border-warning/20">
        <span className="text-xs text-success flex items-center gap-1">
          <Sparkles className="h-3 w-3" />
          HIGH POTENTIAL
        </span>
      </div>
    </div>
  );
}

function CustomTooltip({ active, payload }: any) {
  if (!active || !payload || !payload[0]) return null;

  const data = payload[0].payload as KeywordPoint;
  const isWinner = data.category === 'winner';

  return (
    <div className="glass-panel p-4 shadow-lg border border-border min-w-[200px]">
      <p className="font-medium text-foreground truncate max-w-[250px]">"{data.query}"</p>
      <div className="mt-3 space-y-2 text-sm">
        <div className="flex justify-between gap-6">
          <span className="text-muted-foreground">Impressions:</span>
          <span className="text-foreground font-bold">{data.impressions.toLocaleString()}</span>
        </div>
        <div className="flex justify-between gap-6">
          <span className="text-muted-foreground">Avg Position:</span>
          <span className={cn('font-bold', isWinner ? 'text-success' : 'text-warning')}>
            #{data.position.toFixed(1)}
          </span>
        </div>
        <div className="flex justify-between gap-6">
          <span className="text-muted-foreground">Clicks:</span>
          <span className="text-foreground font-medium">{data.clicks}</span>
        </div>
      </div>
      <div className={cn(
        'mt-3 pt-2 border-t border-border text-xs font-medium flex items-center gap-1',
        isWinner ? 'text-success' : 'text-warning'
      )}>
        <Sparkles className="h-3 w-3" />
        {isWinner ? 'WINNER - Page 1' : 'OPPORTUNITY - Improve to Page 1'}
      </div>
    </div>
  );
}
